// Chapter sidebar: jump-link active-highlighting + offset-aware smooth
// scroll. Same mechanism as the unlock theme's blog "Table of Contents"
// (src/js/theme/pages/single.js) — ported rather than reinvented, adapted to
// this page's single canonical nav (the offcanvas sidebar shows the same
// list at every breakpoint, so there's no separate desktop/mobile nav to
// keep in sync).

document.addEventListener('DOMContentLoaded', () => {
  const nav = document.querySelector('#product-guide-nav-sections');
  if (!nav) return;

  const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
  if (!links.length) return;

  const progressLine = nav.parentElement?.querySelector('.progress-line') || null;
  const article = document.querySelector('.product-guide-content');
  if (!article) return;

  const getOffset = () => {
    const candidates = ['#wrapper-navbar', '.wpadminbar'];
    let h = 0;

    for (const sel of candidates) {
      for (const el of document.querySelectorAll(sel)) {
        const pos = getComputedStyle(el).position;
        if (pos !== 'sticky' && pos !== 'fixed') continue;

        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top <= 1) h += Math.round(rect.height);
      }
    }

    return h + 16;
  };

  let offset = getOffset();
  document.documentElement.style.setProperty('--pg-toc-offset', `${offset}px`);

  const getTargets = () =>
    links
      .map((link) => {
        const id = (link.getAttribute('href') || '').slice(1);
        const el = id ? document.getElementById(id) : null;
        return { id, el };
      })
      .filter((x) => x.el);

  let targets = getTargets();

  const isAtArticleBottom = () => {
    const rect = article.getBoundingClientRect();
    return rect.bottom <= offset + 8;
  };

  const clearAllStates = () => {
    for (const link of links) {
      // .active is Bootstrap's own button/link active-state class (see
      // .btn-link-action in the markup) — no bespoke CSS of our own to keep
      // in sync with this.
      link.classList.remove('active');
      link.removeAttribute('aria-current');
    }

    if (progressLine) progressLine.style.transform = 'scaleY(0)';
  };

  const setStateByIndex = (idx) => {
    for (const [i, link] of links.entries()) {
      link.classList.toggle('active', i === idx);
      if (i === idx) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }

    if (progressLine) {
      progressLine.style.transform = `scaleY(${(idx + 1) / links.length})`;
    }
  };

  const computeActiveIndex = () => {
    if (!targets.length) return -1;
    if (isAtArticleBottom()) return targets.length - 1;

    // Activate a little before it reaches the top line so it feels right.
    const ACTIVATION_LEAD = 60;
    const line = offset + ACTIVATION_LEAD;

    const firstTop = targets[0].el.getBoundingClientRect().top;
    if (firstTop > line) return -1;

    let active = 0;
    for (const [i, target] of targets.entries()) {
      const { top } = target.el.getBoundingClientRect();
      if (top <= line) active = i;
      else break;
    }

    return active;
  };

  let raf = null;
  const sync = () => {
    offset = getOffset();
    document.documentElement.style.setProperty('--pg-toc-offset', `${offset}px`);

    targets = getTargets();

    const idx = computeActiveIndex();
    if (idx === -1) {
      clearAllStates();
      return;
    }

    setStateByIndex(idx);
  };

  window.addEventListener(
    'scroll',
    () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        sync();
      });
    },
    { passive: true }
  );

  nav.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;

    const id = (a.getAttribute('href') || '').slice(1);
    const el = id ? document.getElementById(id) : null;
    if (!el) return;

    e.preventDefault();
    // eslint-disable-next-line no-restricted-globals
    history.pushState(null, '', `#${id}`);

    offset = getOffset();
    document.documentElement.style.setProperty('--pg-toc-offset', `${offset}px`);

    const top = window.scrollY + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top, behavior: 'smooth' });

    setTimeout(sync, 120);
    setTimeout(sync, 300);
    setTimeout(sync, 600);
  });

  window.addEventListener('load', () => sync());
  window.addEventListener('resize', () => {
    clearTimeout(globalThis.__pgTocResizeTimer);
    globalThis.__pgTocResizeTimer = setTimeout(sync, 150);
  });

  sync();
});
